<template>
  <div v-loading="loading">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Clinic Content Moderation</h4>
      <el-alert
        v-if="orphanIds.length"
        type="info"
        :title="`Hidden: ${orphanIds.length} entries for deleted clinics`"
        description="Server-side cleanup requires a DELETE endpoint (not exposed by service-clinics). Rows are filtered out here so they don't clutter the queue."
        show-icon
        :closable="false"
        style="max-width: 560px; margin-left: auto"
      />
    </div>

    <card>
      <el-table :data="sortedList" style="width: 100%" :row-class-name="tableRowClassName">
        <el-table-column label="№" type="index" width="50" />
        <el-table-column label="Date" width="110">
          <template slot-scope="{ row }">
            {{ getTextInfoDate(row.texts) }}
          </template>
        </el-table-column>
        <el-table-column label="Clinic" min-width="220">
          <template slot-scope="{ row }">
            {{ (mapClinicsById[row.clinic_id] && mapClinicsById[row.clinic_id].name) || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="Links" width="130">
          <template slot-scope="{ row }">
            <template v-if="mapClinicsById[row.clinic_id]">
              <a :href="mapClinicsById[row.clinic_id].linkRu" target="_blank" class="mod__link">RU</a>
              <a :href="mapClinicsById[row.clinic_id].linkEn" target="_blank" class="mod__link">EN</a>
              <a :href="mapClinicsById[row.clinic_id].linkEs" target="_blank" class="mod__link">ES</a>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="RU Status" width="140">
          <template slot-scope="{ row }">
            <span :class="statusClass(getStatus(row.texts, 'ru'))">{{ getStatus(row.texts, 'ru') || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column width="90">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEditDialog(row, 'ru')" icon="el-icon-edit">RU</el-button>
          </template>
        </el-table-column>
        <el-table-column label="EN Status" width="140">
          <template slot-scope="{ row }">
            <span :class="statusClass(getStatus(row.texts, 'en'))">{{ getStatus(row.texts, 'en') || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column width="90">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEditDialog(row, 'en')" icon="el-icon-edit">EN</el-button>
          </template>
        </el-table-column>
        <el-table-column label="ES Status" width="140">
          <template slot-scope="{ row }">
            <span :class="statusClass(getStatus(row.texts, 'es'))">{{ getStatus(row.texts, 'es') || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column width="90">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEditDialog(row, 'es')" icon="el-icon-edit">ES</el-button>
          </template>
        </el-table-column>
      </el-table>
    </card>

    <!-- Edit dialog -->
    <el-dialog
      :visible.sync="isDialogOpen"
      :title="`Edit description — ${(choosenLang || '').toUpperCase()}`"
      width="900px"
      class="mod__dialog"
      :destroy-on-close="true"
      append-to-body
    >
      <div v-if="isDialogOpen" :key="`${clinicId}-${choosenLang}`">
        <el-tabs v-model="activeEditorTab">
          <el-tab-pane label="Short description" name="short">
            <ckeditor
              v-if="activeEditorTab === 'short'"
              v-model="editForm.txt_short"
              :config="ckeditorConfig"
              class="mb-3"
            ></ckeditor>
          </el-tab-pane>
          <el-tab-pane label="Full description" name="full">
            <ckeditor
              v-if="activeEditorTab === 'full'"
              v-model="editForm.txt_long"
              :config="ckeditorConfig"
            ></ckeditor>
          </el-tab-pane>
        </el-tabs>
      </div>

      <span slot="footer">
        <el-button @click="isDialogOpen = false">Cancel</el-button>
        <el-button type="info" :loading="saving" @click="saveDescriptionAndUpdate">Save</el-button>
        <el-button type="success" :loading="saving" @click="saveAndApproveDescription">Approve</el-button>
        <el-button type="danger" :loading="saving" @click="saveAndDisapproveDescription">Reject</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import API from "@/api/modules/clinic-content-moderation";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      choosenLang: null,
      clinicId: null,
      mapClinicsById: {},
      orphanIds: [],
      isDialogOpen: false,
      editForm: {
        txt_short: "",
        txt_long: "",
      },
      activeEditorTab: "short",
      ckeditorConfig: { allowedContent: true },
    };
  },
  computed: {
    ...mapState("clinicContentModeration", ["clinicSandboxText"]),
    sortedList() {
      return [...this.clinicSandboxText]
        .filter((el) => !this.orphanIds.includes(el.clinic_id))
        .map((el) => ({
          ...el,
          parsedDate: this.getTextInfoDate(el.texts)
            ? Date.parse(this.getTextInfoDate(el.texts))
            : 0,
        }))
        .sort((a, b) => b.parsedDate - a.parsedDate);
    },
  },
  methods: {
    ...mapActions("clinicContentModeration", [
      "getClinicSandboxText",
      "updateSandboxClinicText",
      "setTextSandboxApproveStatus",
      "setTextSandboxDisapproveStatus",
    ]),
    tableRowClassName({ row }) {
      return this.mapClinicsById[row.clinic_id] && this.mapClinicsById[row.clinic_id].hasActiveAgencies
        ? "mod__row--has-active-agencies"
        : "";
    },
    statusClass(status) {
      if (!status) return "mod__status mod__status--empty";
      const s = String(status).toLowerCase();
      if (s.includes("approv")) return "mod__status mod__status--approved";
      if (s.includes("disapprov") || s.includes("reject")) return "mod__status mod__status--rejected";
      if (s.includes("pending") || s.includes("wait")) return "mod__status mod__status--pending";
      return "mod__status";
    },
    async saveAndApproveDescription() {
      this.saving = true;
      try {
        await this.saveDescription();
        await this.setTextSandboxApproveStatus({ clinicId: this.clinicId, lang: this.choosenLang });
        await this.fetchAll();
        this.$notify({ type: "success", title: "Approved" });
      } catch (e) {
        this.$notify({ type: "warning", title: "Error approving" });
      } finally {
        this.saving = false;
      }
    },
    async saveAndDisapproveDescription() {
      this.saving = true;
      try {
        await this.saveDescription();
        await this.setTextSandboxDisapproveStatus({
          clinicId: this.clinicId,
          lang: this.choosenLang,
          alias: ["txt_long", "txt_short"],
        });
        await this.fetchAll();
        this.$notify({ type: "success", title: "Rejected" });
      } catch (e) {
        this.$notify({ type: "warning", title: "Error rejecting" });
      } finally {
        this.saving = false;
      }
    },
    async saveDescriptionAndUpdate() {
      this.saving = true;
      try {
        await this.saveDescription();
        await this.fetchAll();
        this.$notify({ type: "success", title: "Saved" });
      } catch (e) {
        this.$notify({ type: "warning", title: "Error saving" });
      } finally {
        this.saving = false;
      }
    },
    async saveDescription() {
      await this.updateSandboxClinicText({
        lang: this.choosenLang,
        clinicId: this.clinicId,
        textAlias: "txt_short",
        text: this.editForm.txt_short,
      });
      await this.updateSandboxClinicText({
        lang: this.choosenLang,
        clinicId: this.clinicId,
        textAlias: "txt_long",
        text: this.editForm.txt_long,
      });
      this.isDialogOpen = false;
    },
    openEditDialog(textInfo, lang) {
      this.clinicId = textInfo.clinic_id;
      this.choosenLang = lang;

      let shortText = "";
      let fullText = "";
      const langTexts = textInfo.texts && textInfo.texts[lang];
      if (langTexts && !Array.isArray(langTexts)) {
        const ts = langTexts.txt_short;
        const tl = langTexts.txt_long;
        shortText = (ts && (typeof ts === "string" ? ts : ts.text)) || "";
        fullText = (tl && (typeof tl === "string" ? tl : tl.text)) || "";
      }

      this.editForm.txt_short = shortText;
      this.editForm.txt_long = fullText;
      this.activeEditorTab = "short";
      this.isDialogOpen = true;
    },
    getStatus(texts, lang) {
      if (texts && texts[lang] && !Array.isArray(texts[lang]) && texts[lang].txt_long) {
        return texts[lang].txt_long.status;
      }
      return "";
    },
    getTextInfoDate(texts) {
      let date = "";
      if (texts && texts.en && !Array.isArray(texts.en) && texts.en.txt_long) {
        date = texts.en.txt_long.updated_at;
      } else if (texts && texts.ru && !Array.isArray(texts.ru) && texts.ru.txt_long) {
        date = texts.ru.txt_long.updated_at;
      } else if (texts && texts.es && !Array.isArray(texts.es) && texts.es.txt_long) {
        date = texts.es.txt_long.updated_at;
      }
      return date ? date.slice(0, 10) : "";
    },
    async fetchAll() {
      this.loading = true;
      try {
        await this.getClinicSandboxText();
        const obj = {};
        const orphans = [];
        const ids = this.clinicSandboxText.map((item) => item.clinic_id);

        const responses = await Promise.all(
          ids.map((id) =>
            API.getAdminClinicInfoById(id)
              .then((res) => ({ id, res }))
              .catch((err) => ({ id, res: null, status: err?.response?.status }))
          )
        );

        responses.forEach(({ id, res, status }) => {
          const clinic = res && res.data;
          if (clinic && !obj[clinic.id]) {
            obj[clinic.id] = {
              name: (clinic.name && (clinic.name.ru || clinic.name.en)) || clinic.alias?.ru || `#${clinic.id}`,
              linkRu: clinic.alias?.ru ? `https://ru.bookimed.com/clinic/${clinic.alias.ru}/` : "#",
              linkEn: clinic.alias?.en ? `https://bookimed.com/clinic/${clinic.alias.en}/` : "#",
              linkEs: clinic.alias?.en ? `https://es.bookimed.com/clinic/${clinic.alias.en}/` : "#",
              hasActiveAgencies: clinic.has_active_agencies === true,
            };
          } else if (status === 404 || !res) {
            orphans.push(id);
          }
        });

        this.mapClinicsById = obj;
        this.orphanIds = Array.from(new Set(orphans));
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchAll();
  },
};
</script>

<style scoped>
.mod__link {
  display: inline-block;
  cursor: pointer;
  border-radius: 4px;
  background-color: #3c8dbc;
  color: #fff;
  border: 0;
  text-decoration: none;
  padding: 1px 6px;
  margin-right: 3px;
  font-size: 12px;
}
.mod__link:hover {
  background-color: #2c7eaf;
  color: #fff;
}
.mod__subtitle {
  font-weight: 700;
  margin: 10px 0;
}
.mod__status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}
.mod__status--approved {
  background: #a3cc0e;
  color: white;
}
.mod__status--rejected {
  background: #fb765f;
  color: white;
}
.mod__status--pending {
  background: #f39c12;
  color: white;
}
.mod__status--empty {
  color: #999;
}
::v-deep .mod__row--has-active-agencies td {
  background-color: #eaffea !important;
}
</style>
